var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { Badge } from "../../tremor/Badge";
import { Label, Metric } from "../../tremor/Text";
import { Button } from "../../tremor/Button";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useDashboard } from "../../layouts/Dashboard/useDashboard";
import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";
import { ArrowDownTrayIcon, ArrowLeftIcon, ArrowPathIcon, ArrowRightIcon, Bars3Icon, ClockIcon, DocumentChartBarIcon, FunnelIcon, PencilIcon, PhotoIcon, PlusIcon, PresentationChartBarIcon, TrashIcon, } from "@heroicons/react/24/outline";
import { toast } from "sonner";
import { useBackend } from "../../layouts/Wrapper";
import { DropdownMenu, DropdownMenuContent, DropdownMenuGroup, DropdownMenuIconWrapper, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger, } from "../../tremor/DropdownMenu";
import { useTheme } from "../../layouts/Dashboard/useTheme";
import { Popover, PopoverContent, PopoverTrigger } from "../../tremor/Popover";
import { FilterBar } from "../FilterBar";
import { useAutomationsModal } from "../AutomationsModal/useAutomationsModal";
import { Tooltip } from "../../tremor/Tooltip";
import { DragDropContext, Droppable, Draggable } from '@hello-pangea/dnd';
import { Text } from "../../tremor/Text";
import { EyeIcon, EyeSlashIcon } from "@heroicons/react/20/solid";
import { Icon } from "../../tremor/Icon";
dayjs.extend(relativeTime);
function formatList(items) {
    if (items.length === 0)
        return React.createElement(React.Fragment, null);
    if (items.length === 1)
        return React.createElement(Badge, { variant: "neutral", className: "!onvo-py-0 onvo-mb-1" }, items[0]);
    if (items.length === 2)
        return React.createElement(React.Fragment, null,
            React.createElement(Badge, { variant: "neutral", className: "!onvo-py-0 onvo-mb-1" }, items[0]),
            " and ",
            React.createElement(Badge, { variant: "neutral", className: "!onvo-py-0 onvo-mb-1" }, items[1]));
    return React.createElement(React.Fragment, null,
        items.slice(0, -1).map(a => React.createElement(Badge, { key: a, variant: "neutral", className: "!onvo-py-0 onvo-mb-1 onvo-mr-1" }, a)),
        "and ",
        React.createElement(Badge, { key: items[items.length - 1], variant: "neutral", className: "!onvo-py-0 onvo-mb-1" }, items[items.length - 1]));
}
export const LastUpdatedBadge = ({}) => {
    const { dashboard, refreshing } = useDashboard();
    const [key, setKey] = useState((dashboard === null || dashboard === void 0 ? void 0 : dashboard.last_updated_at) || new Date().toISOString());
    useEffect(() => {
        let interval = setInterval(() => {
            setKey(new Date().toISOString());
        }, 1000);
        return () => {
            clearInterval(interval);
        };
    }, []);
    if (refreshing) {
        return (React.createElement(Badge, { key: "refreshing-badge", className: "onvo-last-updated-badge onvo-justify-center onvo-items-center" },
            React.createElement(ArrowPathIcon, { className: "onvo-size-3 onvo-animate-spin onvo-w-full" }),
            "Auto refreshing..."));
    }
    return (React.createElement(Tooltip, { side: "bottom", align: "end", content: "The dashboard was last updated " + dayjs(key).fromNow() },
        React.createElement(Badge, { key: key, className: "onvo-last-updated-badge onvo-justify-center onvo-items-center onvo-w-full" },
            React.createElement(ArrowPathIcon, { className: "onvo-size-3" }),
            dayjs(key).fromNow())));
};
export const DashboardHeaderTab = ({ item }) => {
    var _a;
    const [editing, setEditing] = useState(false);
    const [title, setTitle] = useState(item.label);
    const componentRef = useRef(null);
    const { backend, adminMode } = useBackend();
    const { dashboard, setDashboard, widgets } = useDashboard();
    const { containerRef } = useBackend();
    const updateDashboard = () => {
        setEditing(false);
        if (dashboard) {
            const newTabs = dashboard.tabs.map((tab) => {
                if (tab.id === item.id) {
                    return Object.assign(Object.assign({}, tab), { label: title });
                }
                return tab;
            });
            const newDashboard = Object.assign(Object.assign({}, dashboard), { tabs: newTabs });
            setDashboard(newDashboard);
            backend === null || backend === void 0 ? void 0 : backend.dashboards.update(dashboard.id, newDashboard);
        }
    };
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (componentRef.current && !componentRef.current.contains(event.target)) {
                updateDashboard();
            }
        };
        if (editing) {
            document.addEventListener('mousedown', handleClickOutside);
        }
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [editing]);
    const moveTab = (position) => {
        if (dashboard) {
            const currentIndex = dashboard.tabs.findIndex(tab => tab.id === item.id);
            if (currentIndex === -1)
                return;
            const newTabs = [...dashboard.tabs];
            let newIndex;
            if (position === "left") {
                newIndex = Math.max(0, currentIndex - 1);
            }
            else {
                newIndex = Math.min(newTabs.length - 1, currentIndex + 1);
            }
            // Only move if the new position is different
            if (newIndex !== currentIndex) {
                // Remove the tab from its current position
                const [movedTab] = newTabs.splice(currentIndex, 1);
                // Insert it at the new position
                newTabs.splice(newIndex, 0, movedTab);
                const newDashboard = Object.assign(Object.assign({}, dashboard), { tabs: newTabs });
                setDashboard(newDashboard);
                backend === null || backend === void 0 ? void 0 : backend.dashboards.update(dashboard.id, newDashboard);
            }
        }
    };
    const deleteTab = () => {
        if (dashboard) {
            const widgetsExist = widgets === null || widgets === void 0 ? void 0 : widgets.some(widget => widget.tab === item.id);
            if (widgetsExist) {
                toast.error("You cannot delete a tab with widgets, delete all the widgets in the tab and try again");
                return;
            }
            const newTabs = dashboard.tabs.filter((tab) => tab.id !== item.id);
            const newDashboard = Object.assign(Object.assign({}, dashboard), { tabs: newTabs });
            setDashboard(newDashboard);
            backend === null || backend === void 0 ? void 0 : backend.dashboards.update(dashboard.id, newDashboard);
        }
    };
    const editable = adminMode || ((_a = dashboard === null || dashboard === void 0 ? void 0 : dashboard.settings) === null || _a === void 0 ? void 0 : _a.can_edit_tabs);
    return (React.createElement("div", { ref: componentRef, onDoubleClick: () => {
            if (editable) {
                setEditing(true);
            }
        } }, editing ? React.createElement("input", { className: "onvo-text-sm onvo-rounded-md onvo-px-1 onvo-py-1 onvo-border-blue-500", value: title, onKeyUp: (e) => {
            if (e.key === "Enter") {
                updateDashboard();
            }
        }, onChange: (e) => {
            setTitle(e.target.value);
        } }) :
        React.createElement("div", { className: "onvo-flex onvo-flex-row onvo-items-center onvo-gap-1" },
            React.createElement(Text, null, item.label),
            editable && (React.createElement(DropdownMenu, null,
                React.createElement(DropdownMenuTrigger, { asChild: true },
                    React.createElement("div", { className: "onvo-cursor-pointer hover:onvo-bg-black/10 dark:hover:onvo-bg-white/10 onvo-rounded-full onvo-size-5 onvo-flex onvo-items-center onvo-justify-center onvo-transition-all" },
                        React.createElement("svg", { className: "onvo-size-2 onvo-fill-slate-500 dark:onvo-fill-slate-400", fill: "#000000", version: "1.1", id: "Capa_1", xmlns: "http://www.w3.org/2000/svg", xmlnsXlink: "http://www.w3.org/1999/xlink", viewBox: "0 0 96.154 96.154", xmlSpace: "preserve" },
                            React.createElement("g", { id: "SVGRepo_bgCarrier", strokeWidth: "0" }),
                            React.createElement("g", { id: "SVGRepo_tracerCarrier", strokeLinecap: "round", strokeLinejoin: "round" }),
                            React.createElement("g", { id: "SVGRepo_iconCarrier" },
                                " ",
                                React.createElement("g", null,
                                    " ",
                                    React.createElement("path", { d: "M0.561,20.971l45.951,57.605c0.76,0.951,2.367,0.951,3.127,0l45.956-57.609c0.547-0.689,0.709-1.716,0.414-2.61 c-0.061-0.187-0.129-0.33-0.186-0.437c-0.351-0.65-1.025-1.056-1.765-1.056H2.093c-0.736,0-1.414,0.405-1.762,1.056 c-0.059,0.109-0.127,0.253-0.184,0.426C-0.15,19.251,0.011,20.28,0.561,20.971z" }),
                                    " "),
                                " ")))),
                React.createElement(DropdownMenuContent, { side: "bottom", align: "start", container: containerRef === null || containerRef === void 0 ? void 0 : containerRef.current, className: "!onvo-backdrop-blur-lg onvo-min-w-56 onvo-z-50" },
                    React.createElement("div", { className: "onvo-w-full onvo-absolute onvo-top-0 onvo-left-0 onvo-right-0 onvo-bottom-0 onvo-foreground-color onvo-opacity-50 onvo-rounded-lg -onvo-z-10" }),
                    React.createElement(DropdownMenuGroup, null,
                        React.createElement(DropdownMenuItem, { onClick: () => setEditing(true) },
                            React.createElement("span", { className: "onvo-flex onvo-items-center onvo-gap-x-2" },
                                React.createElement(DropdownMenuIconWrapper, null,
                                    React.createElement(PencilIcon, { className: "onvo-size-4 onvo-text-inherit" })),
                                React.createElement("span", null, "Rename")))),
                    React.createElement(DropdownMenuSeparator, null),
                    React.createElement(DropdownMenuGroup, null,
                        React.createElement(DropdownMenuItem, { onClick: () => moveTab("left") },
                            React.createElement("span", { className: "onvo-flex onvo-items-center onvo-gap-x-2" },
                                React.createElement(DropdownMenuIconWrapper, null,
                                    React.createElement(ArrowLeftIcon, { className: "onvo-size-4 onvo-text-inherit" })),
                                React.createElement("span", null, "Move Left"))),
                        React.createElement(DropdownMenuItem, { onClick: () => moveTab("right") },
                            React.createElement("span", { className: "onvo-flex onvo-items-center onvo-gap-x-2" },
                                React.createElement(DropdownMenuIconWrapper, null,
                                    React.createElement(ArrowRightIcon, { className: "onvo-size-4 onvo-text-inherit" })),
                                React.createElement("span", null, "Move Right")))),
                    React.createElement(DropdownMenuSeparator, null),
                    React.createElement(DropdownMenuGroup, null,
                        React.createElement(DropdownMenuItem, { onClick: () => deleteTab() },
                            React.createElement("span", { className: "onvo-flex onvo-items-center onvo-gap-x-2 onvo-text-red-500" },
                                React.createElement(DropdownMenuIconWrapper, null,
                                    React.createElement(TrashIcon, { className: "onvo-size-4 onvo-text-red-500 onvo-text-inherit" })),
                                React.createElement("span", null, "Delete"))))))))));
};
export const DashboardHeader = ({ children, className, variant }) => {
    var _a, _b, _c, _d, _e, _f;
    const headerRef = useRef(null);
    const [headerHeight, setHeaderHeight] = useState(0);
    // Effect to measure and update header height
    useEffect(() => {
        if (headerRef.current) {
            const resizeObserver = new ResizeObserver(entries => {
                for (const entry of entries) {
                    setHeaderHeight(entry.contentRect.height);
                }
            });
            resizeObserver.observe(headerRef.current);
            return () => resizeObserver.disconnect();
        }
    }, []);
    const { setOpen } = useAutomationsModal();
    const { dashboard, tab, setTab, progress, refreshing, setDashboard } = useDashboard();
    const theme = useTheme();
    const { backend, adminMode, containerRef } = useBackend();
    const exportDashboard = useCallback((format) => {
        if (!backend || !dashboard) {
            return;
        }
        toast.promise(() => __awaiter(void 0, void 0, void 0, function* () {
            return backend.dashboard(dashboard.id).export(format, theme, tab, (event) => {
                // console.log(event);
            });
        }), {
            loading: `Exporting dashboard as ${format}...`,
            success: (output) => __awaiter(void 0, void 0, void 0, function* () {
                try {
                    // Get the file URL, replacing host.docker.internal with localhost if needed
                    const fileUrl = output.url.replace("host.docker.internal", "localhost");
                    // Fetch the file as a blob
                    const response = yield fetch(fileUrl);
                    if (!response.ok)
                        throw new Error('Network response was not ok');
                    // Get the blob from the response
                    const blob = yield response.blob();
                    // Create a blob URL from the blob
                    const blobUrl = window.URL.createObjectURL(blob);
                    // Create a link element
                    const link = document.createElement('a');
                    link.href = blobUrl;
                    link.download = output.fileName || `export.${format}`; // Set the filename
                    link.style.display = 'none';
                    // Add to document, click and remove
                    document.body.appendChild(link);
                    link.click();
                    // Clean up
                    setTimeout(() => {
                        document.body.removeChild(link);
                        window.URL.revokeObjectURL(blobUrl); // Release the blob URL
                    }, 100);
                }
                catch (error) {
                    console.error('Download failed:', error);
                    toast.error('Download failed. Please try again.');
                }
                return "Dashboard exported successfully";
            }),
            error: (e) => {
                return "Failed to export dashboard: " + e.message;
            },
        });
    }, [dashboard, backend, tab]);
    const ImageDownloadEnabled = useMemo(() => {
        if ((dashboard === null || dashboard === void 0 ? void 0 : dashboard.settings) && dashboard.settings.disable_download_images)
            return false;
        return true;
    }, [dashboard]);
    const DocumentDownloadEnabled = useMemo(() => {
        if ((dashboard === null || dashboard === void 0 ? void 0 : dashboard.settings) && dashboard.settings.disable_download_documents)
            return false;
        return true;
    }, [dashboard]);
    const ReportDownloadEnabled = useMemo(() => {
        if ((dashboard === null || dashboard === void 0 ? void 0 : dashboard.settings) && dashboard.settings.disable_download_reports)
            return false;
        return true;
    }, [dashboard]);
    const filtered = useMemo(() => {
        let filters = (dashboard === null || dashboard === void 0 ? void 0 : dashboard.filters) || [];
        if (filters.length === 0)
            return [];
        let results = [];
        for (let filter of filters) {
            if (filter.type === "text" && filter.options.length !== filter.values.length) {
                results.push(filter.label);
            }
            if (filter.type === "number" && (filter.options[0] !== filter.values[0] || filter.options[1] !== filter.values[1])) {
                results.push(filter.label);
            }
            if (filter.type === "date" && (filter.options[0] !== filter.values[0] || filter.options[1] !== filter.values[1])) {
                results.push(filter.label);
            }
        }
        return results;
    }, [dashboard]);
    const border_radius = useMemo(() => {
        var _a, _b;
        return ((_a = dashboard === null || dashboard === void 0 ? void 0 : dashboard.settings) === null || _a === void 0 ? void 0 : _a.widget_border_radius) === undefined
            ? 16
            : (_b = dashboard === null || dashboard === void 0 ? void 0 : dashboard.settings) === null || _b === void 0 ? void 0 : _b.widget_border_radius;
    }, [dashboard]);
    const padding = useMemo(() => {
        var _a, _b;
        return ((_a = dashboard === null || dashboard === void 0 ? void 0 : dashboard.settings) === null || _a === void 0 ? void 0 : _a.widget_padding) === undefined
            ? 10
            : (_b = dashboard === null || dashboard === void 0 ? void 0 : dashboard.settings) === null || _b === void 0 ? void 0 : _b.widget_padding;
    }, [dashboard]);
    if (((_a = dashboard === null || dashboard === void 0 ? void 0 : dashboard.settings) === null || _a === void 0 ? void 0 : _a.hide_header) && !adminMode)
        return React.createElement(React.Fragment, null);
    const DownloadDropdown = (React.createElement(DropdownMenu, { modal: false },
        React.createElement(DropdownMenuTrigger, { asChild: true },
            React.createElement(Button, { className: "onvo-foreground-color onvo-border-black/10 dark:onvo-border-white/10", variant: "secondary", disabled: !dashboard },
                React.createElement(ArrowDownTrayIcon, { className: "onvo-size-4" }))),
        React.createElement(DropdownMenuContent, { side: "bottom", align: "end", container: containerRef === null || containerRef === void 0 ? void 0 : containerRef.current, className: "onvo-min-w-56 onvo-backdrop-blur-lg onvo-border-black/10 dark:onvo-border-white/10" },
            React.createElement("div", { className: "onvo-w-full onvo-absolute onvo-top-0 onvo-left-0 onvo-right-0 onvo-bottom-0 onvo-foreground-color onvo-opacity-50 onvo-rounded-lg -onvo-z-10" }),
            ReportDownloadEnabled && (React.createElement(React.Fragment, null,
                React.createElement(DropdownMenuLabel, null, "Report formats"),
                React.createElement(DropdownMenuGroup, null,
                    React.createElement(DropdownMenuItem, { onClick: () => exportDashboard("xlsx") },
                        React.createElement("span", { className: "onvo-flex onvo-items-center onvo-gap-x-2" },
                            React.createElement(DocumentChartBarIcon, { className: "onvo-size-4" }),
                            React.createElement("span", null, "Excel"))),
                    React.createElement(DropdownMenuItem, { onClick: () => exportDashboard("csv") },
                        React.createElement("span", { className: "onvo-flex onvo-items-center onvo-gap-x-2" },
                            React.createElement(DropdownMenuIconWrapper, null,
                                React.createElement(DocumentChartBarIcon, { className: "onvo-size-4" })),
                            React.createElement("span", null, "CSV")))))),
            DocumentDownloadEnabled && ReportDownloadEnabled && (React.createElement(DropdownMenuSeparator, { className: "onvo-border-black/10 dark:onvo-border-white/10" })),
            DocumentDownloadEnabled && (React.createElement(React.Fragment, null,
                React.createElement(DropdownMenuLabel, null, "Document formats"),
                React.createElement(DropdownMenuGroup, null,
                    React.createElement(DropdownMenuItem, { onClick: () => exportDashboard("pdf") },
                        React.createElement("span", { className: "onvo-flex onvo-items-center onvo-gap-x-2" },
                            React.createElement(DocumentChartBarIcon, { className: "onvo-size-4" }),
                            React.createElement("span", null, "PDF"))),
                    React.createElement(DropdownMenuItem, { onClick: () => exportDashboard("pptx") },
                        React.createElement("span", { className: "onvo-flex onvo-items-center onvo-gap-x-2" },
                            React.createElement(PresentationChartBarIcon, { className: "onvo-size-4" }),
                            React.createElement("span", null, "Powerpoint")))))),
            ImageDownloadEnabled && (DocumentDownloadEnabled || ReportDownloadEnabled) && (React.createElement(DropdownMenuSeparator, { className: "onvo-border-black/10 dark:onvo-border-white/10" })),
            ImageDownloadEnabled && (React.createElement(React.Fragment, null,
                React.createElement(DropdownMenuLabel, null, "Image formats"),
                React.createElement(DropdownMenuGroup, null,
                    React.createElement(DropdownMenuItem, { onClick: () => exportDashboard("png") },
                        React.createElement("span", { className: "onvo-flex onvo-items-center onvo-gap-x-2" },
                            React.createElement(DropdownMenuIconWrapper, null,
                                React.createElement(PhotoIcon, { className: "onvo-size-4" })),
                            React.createElement("span", null, "PNG"))),
                    React.createElement(DropdownMenuItem, { onClick: () => exportDashboard("jpeg") },
                        React.createElement("span", { className: "onvo-flex onvo-items-center onvo-gap-x-2" },
                            React.createElement(DropdownMenuIconWrapper, null,
                                React.createElement(PhotoIcon, { className: "onvo-size-4" })),
                            React.createElement("span", null, "JPEG")))))))));
    if (variant === "pdf" || variant === "pptx") {
        return (React.createElement(React.Fragment, null,
            React.createElement("div", { style: { height: headerHeight }, className: "onvo-w-full onvo-flex-shrink-0" }),
            React.createElement("section", { ref: headerRef, className: `onvo-@container/onvo-dashboard-header onvo-leading-tight onvo-bg-slate-900 onvo-dashboard-header onvo-absolute onvo-z-50 onvo-top-0 onvo-left-0 onvo-right-0 ${className ? className : ""}` },
                React.createElement("div", { className: "onvo-flex onvo-py-2 onvo-px-5 onvo-flex-row onvo-justify-between onvo-items-center" },
                    ((dashboard === null || dashboard === void 0 ? void 0 : dashboard.tabs) || []).filter(a => !a.hidden).length > 1 ? (React.createElement("div", { className: "onvo-flex onvo-flex-row onvo-gap-2" }, ((dashboard === null || dashboard === void 0 ? void 0 : dashboard.tabs) || []).filter(a => !a.hidden).map(a => (React.createElement(Button, { key: a.id + "", variant: a.id === tab ? "light" : "secondary", onClick: () => {
                            setTab(a.id);
                        } }, a.label))))) : React.createElement("div", null),
                    (ImageDownloadEnabled || ReportDownloadEnabled || DocumentDownloadEnabled) &&
                        DownloadDropdown))));
    }
    const reorder = (list, startIndex, endIndex) => {
        const result = Array.from(list);
        const [removed] = result.splice(startIndex, 1);
        result.splice(endIndex, 0, removed);
        return result;
    };
    const onDragEnd = (result) => {
        if (!result.destination || !dashboard) {
            return;
        }
        const items = reorder((dashboard === null || dashboard === void 0 ? void 0 : dashboard.tabs) || [], result.source.index, result.destination.index);
        const updatedDashboard = Object.assign(Object.assign({}, dashboard), { tabs: items });
        setDashboard(updatedDashboard);
        backend === null || backend === void 0 ? void 0 : backend.dashboards.update(dashboard.id, updatedDashboard);
    };
    const addTab = () => {
        if (!dashboard)
            return;
        if (!dashboard.tabs || dashboard.tabs.length === 0) {
            const newTabs = [{
                    id: 0,
                    label: "New tab"
                }];
            const newDashboard = Object.assign(Object.assign({}, dashboard), { tabs: newTabs });
            setDashboard(newDashboard);
            backend === null || backend === void 0 ? void 0 : backend.dashboards.update(dashboard.id, newDashboard);
        }
        else {
            const maxTab = Math.max(...dashboard.tabs.map(a => a.id), 0) + 1;
            const newTabs = [...dashboard.tabs, {
                    id: maxTab,
                    label: "New tab"
                }];
            const newDashboard = Object.assign(Object.assign({}, dashboard), { tabs: newTabs });
            setDashboard(newDashboard);
            backend === null || backend === void 0 ? void 0 : backend.dashboards.update(dashboard.id, newDashboard);
        }
    };
    const hideTab = (tabId, hidden) => {
        if (!dashboard)
            return;
        const newTabs = dashboard.tabs.map(a => {
            if (a.id === tabId) {
                return Object.assign(Object.assign({}, a), { hidden });
            }
            return a;
        });
        const newDashboard = Object.assign(Object.assign({}, dashboard), { tabs: newTabs });
        setDashboard(newDashboard);
        backend === null || backend === void 0 ? void 0 : backend.dashboards.update(dashboard.id, newDashboard);
    };
    const editable = adminMode || ((_b = dashboard === null || dashboard === void 0 ? void 0 : dashboard.settings) === null || _b === void 0 ? void 0 : _b.can_edit_tabs);
    return (React.createElement(React.Fragment, null,
        React.createElement("div", { style: {
                height: (headerHeight) + "px",
                marginTop: padding + "px",
                marginLeft: padding + "px",
                width: "calc(100% - " + (2 * padding) + "px)",
            }, className: "onvo-w-full onvo-flex-shrink-0" }),
        React.createElement("section", { ref: headerRef, className: `onvo-@container/onvo-dashboard-header onvo-leading-tight onvo-dashboard-header onvo-backdrop-blur-lg onvo-absolute onvo-z-50 onvo-shadow-sm onvo-border-solid onvo-border onvo-rounded-2xl onvo-border-black/10 dark:onvo-border-white/10 ${className ? className : ""}`, style: {
                borderRadius: border_radius + "px",
                left: padding + "px",
                right: padding + "px",
                top: padding + "px",
            } },
            React.createElement("div", { className: "onvo-w-full -onvo-z-10 onvo-absolute onvo-top-0 onvo-left-0 onvo-right-0 onvo-bottom-0 onvo-foreground-color onvo-opacity-50", style: { borderRadius: border_radius + "px" } }),
            React.createElement("main", { className: "onvo-mx-auto onvo-px-4 onvo-pt-3 onvo-pb-0 onvo-z-10 onvo-relative onvo-flex onvo-flex-col onvo-items-start onvo-justify-between onvo-gap-4 @lg/onvo-dashboard-header:onvo-flex-row @lg/onvo-dashboard-header:onvo-items-start" },
                React.createElement("div", { className: "onvo-flex-grow onvo-flex onvo-flex-row onvo-gap-3" },
                    dashboard && dashboard.thumbnail && dashboard.thumbnail.trim() !== "" && (React.createElement("img", { className: "onvo-rounded-lg onvo-size-14 onvo-bg-slate-50 onvo-object-contain dark:onvo-bg-slate-900 onvo-flex-shrink-0 onvo-border onvo-border-slate-200 dark:onvo-border-slate-700", src: dashboard.thumbnail, alt: dashboard.title })),
                    React.createElement("div", { className: "onvo-flex-grow" }, dashboard ? (React.createElement(React.Fragment, null,
                        React.createElement(Metric, { className: "onvo-dashboard-header-title !onvo-font-semibold dark:onvo-text-white onvo-text-black onvo-font-override onvo-text-xl" }, (dashboard === null || dashboard === void 0 ? void 0 : dashboard.title) || ""),
                        React.createElement(Label, { className: "onvo-dashboard-header-description !onvo-font-normal onvo-font-override" }, (dashboard === null || dashboard === void 0 ? void 0 : dashboard.description) || ""))) : (React.createElement("div", { className: "onvo-w-3/5 onvo-animate-pulse" },
                        React.createElement("div", { className: "onvo-mb-2 onvo-h-6 onvo-w-4/5 onvo-rounded-md onvo-bg-gray-100 dark:onvo-bg-gray-700" }),
                        React.createElement("div", { className: "onvo-h-4 onvo-w-2/5 onvo-rounded-md onvo-bg-gray-100 dark:onvo-bg-gray-700" }))))),
                React.createElement("div", { className: "onvo-gap-2 onvo-flex onvo-flex-col" },
                    React.createElement(LastUpdatedBadge, null),
                    React.createElement("div", { className: "onvo-flex onvo-flex-row onvo-gap-2 onvo-justify-end" },
                        ((_c = dashboard === null || dashboard === void 0 ? void 0 : dashboard.settings) === null || _c === void 0 ? void 0 : _c.filters) && dashboard.filters.length > 0 && (React.createElement(Popover, null,
                            React.createElement(PopoverTrigger, { asChild: true },
                                React.createElement(Button, { className: "onvo-relative onvo-foreground-color onvo-border-black/10 dark:onvo-border-white/10", variant: "secondary" },
                                    React.createElement(FunnelIcon, { className: "onvo-size-4" }),
                                    (filtered === null || filtered === void 0 ? void 0 : filtered.length) > 0 && (React.createElement(React.Fragment, null,
                                        React.createElement("div", { className: "onvo-size-2" }),
                                        React.createElement(Tooltip, { side: "bottom", sideOffset: 20, content: React.createElement("p", { className: "onvo-text-xs" },
                                                "The dashboard is currently filtered by ",
                                                formatList(filtered)) },
                                            React.createElement("div", { className: "onvo-absolute -onvo-top-2 -onvo-right-2 onvo-rounded-full onvo-bg-red-500 onvo-text-white onvo-text-xs onvo-text-center onvo-font-bold onvo-size-5 onvo-flex onvo-items-center onvo-justify-center" }, filtered === null || filtered === void 0 ? void 0 : filtered.length)))))),
                            React.createElement(PopoverContent, { side: "bottom", align: "end", onInteractOutside: e => e.preventDefault(), container: containerRef === null || containerRef === void 0 ? void 0 : containerRef.current, className: "!onvo-p-0 onvo-z-50 onvo-w-[320px] onvo-backdrop-blur-lg !onvo-bg-transparent" },
                                React.createElement("div", { className: "onvo-w-full onvo-absolute onvo-top-0 onvo-left-0 onvo-right-0 onvo-bottom-0 onvo-foreground-color onvo-opacity-50 onvo-rounded-lg -onvo-z-10" }),
                                React.createElement(FilterBar, null)))),
                        ((_d = dashboard === null || dashboard === void 0 ? void 0 : dashboard.settings) === null || _d === void 0 ? void 0 : _d.can_schedule_reports) && (React.createElement(Tooltip, { content: "Schedule reports", side: "bottom", align: "end" },
                            React.createElement(Button, { className: "onvo-foreground-color onvo-border-black/10 dark:onvo-border-white/10", variant: "secondary", onClick: () => {
                                    setOpen(true);
                                } },
                                React.createElement(ClockIcon, { className: "onvo-size-4" })))),
                        (ImageDownloadEnabled || ReportDownloadEnabled || DocumentDownloadEnabled) &&
                            DownloadDropdown,
                        children))),
            React.createElement("div", { className: "onvo-w-full onvo-h-2" }),
            refreshing && React.createElement("div", { className: "onvo-w-full onvo-overflow-hidden onvo-left-0 onvo-right-0 onvo-h-2" + (((_e = dashboard === null || dashboard === void 0 ? void 0 : dashboard.settings) === null || _e === void 0 ? void 0 : _e.enable_tabs) ? "" : " onvo-rounded-b-2xl") },
                React.createElement("div", { className: "onvo-h-2 onvo-bg-blue-500 onvo-rounded-r-2xl onvo-transition-all onvo-duration-300 onvo-ease-in-out", style: { width: (progress * 100) + "%" } })),
            ((_f = dashboard === null || dashboard === void 0 ? void 0 : dashboard.settings) === null || _f === void 0 ? void 0 : _f.enable_tabs) && (React.createElement("div", { className: "onvo-h-full onvo-border-t onvo-flex onvo-flex-row  onvo-border-black/10 dark:onvo-border-white/10  onvo-rounded-b-2xl" },
                React.createElement(DragDropContext, { onDragEnd: onDragEnd },
                    React.createElement(Droppable, { droppableId: "droppable", direction: "horizontal" }, (provided, snapshot) => (React.createElement("div", Object.assign({ ref: provided.innerRef, className: "onvo-flex onvo-overflow-x-auto onvo-flex-grow onvo-rounded-b-2xl" }, provided.droppableProps),
                        ((dashboard === null || dashboard === void 0 ? void 0 : dashboard.tabs) || []).filter(a => !a.hidden).map((item, index) => (React.createElement(Draggable, { isDragDisabled: !editable, key: item.id, draggableId: item.id + "", index: index }, (provided, snapshot) => (React.createElement("div", Object.assign({ key: item.id + "", ref: provided.innerRef }, provided.draggableProps, provided.dragHandleProps, { className: "onvo-flex onvo-flex-row onvo-items-center onvo-border-r onvo-border-slate-200 dark:onvo-border-slate-800 onvo-gap-2 onvo-py-1 onvo-px-2 onvo-select-none " +
                                (tab === item.id ? "onvo-bg-blue-500/20 " : "") +
                                (snapshot.isDragging ? "onvo-bg-black/10 dark:onvo-bg-white/10" : ""), onClick: () => {
                                setTab(item.id);
                            } }),
                            React.createElement(DashboardHeaderTab, { item: item })))))),
                        provided.placeholder)))),
                React.createElement("div", { className: "onvo-flex onvo-flex-row onvo-items-center onvo-gap-2 onvo-py-1 onvo-px-2 onvo-select-none onvo-border-l onvo-border-black/10 dark:onvo-border-white/10" },
                    editable && (React.createElement(Tooltip, { side: "bottom", content: "Add tab" },
                        React.createElement(Icon, { className: "onvo-cursor-pointer hover:onvo-bg-black/10 dark:onvo-bg-white/10 onvo-rounded-full onvo-transition-all", icon: PlusIcon, onClick: () => {
                                addTab();
                            } }))),
                    React.createElement(DropdownMenu, null,
                        React.createElement(DropdownMenuTrigger, { asChild: true },
                            React.createElement(Icon, { className: "onvo-cursor-pointer hover:onvo-bg-black/10 dark:onvo-bg-white/10 onvo-rounded-full onvo-transition-all", icon: Bars3Icon })),
                        React.createElement(DropdownMenuContent, { side: "bottom", align: "end", container: containerRef === null || containerRef === void 0 ? void 0 : containerRef.current, className: "!onvo-backdrop-blur-lg onvo-min-w-56 onvo-z-50" },
                            React.createElement("div", { className: "onvo-w-full onvo-absolute onvo-top-0 onvo-left-0 onvo-right-0 onvo-bottom-0 onvo-foreground-color onvo-opacity-50 onvo-rounded-lg -onvo-z-10" }),
                            React.createElement(DropdownMenuGroup, null, ((dashboard === null || dashboard === void 0 ? void 0 : dashboard.tabs) || []).map(a => (React.createElement(DropdownMenuItem, { key: a.id, className: "!onvo-py-1", onClick: () => {
                                    setTab(a.id);
                                } },
                                React.createElement(Text, { className: "onvo-flex-grow" }, a.label),
                                React.createElement("div", { className: "onvo-rounded-full onvo-size-6 onvo-flex onvo-items-center onvo-justify-center onvo-mr-2 hover:onvo-bg-black/10 hover:dark:onvo-bg-white/10 onvo-cursor-pointer" }, a.hidden ? React.createElement(EyeSlashIcon, { onClick: () => {
                                        if (editable) {
                                            hideTab(a.id, false);
                                        }
                                    }, className: "onvo-size-4" }) : React.createElement(EyeIcon, { onClick: () => {
                                        if (editable) {
                                            hideTab(a.id, true);
                                        }
                                    }, className: "onvo-size-4" }))))))))))))));
};
//# sourceMappingURL=DashboardHeader.js.map